import { useOutletContext, useSearchParams } from "react-router-dom";
import Button from "../../../form/Button";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useClipboard } from "../../../../util/Flag";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  organizationTeamDashboard,
  organizationTeamDetails,
} from "../../../../services/organizationService";
import { lngDetect } from "../../../../helpers/lngDetect";
import { useDebounce } from "../../../../hooks/useDebounce";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { orderList } from "../../../../services/orderService";
import OrderEarningDrawer from "../../front/promoter/OrderEarning/Drawer/OrderEarningDrawer";
import { handleFilterChange } from "../../../../helpers";
import Table from "../../../elements/table/Table";
import { t } from "i18next";

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      active: {
        label: "Active",
        bgColor: "bg-emerald-50",
        textColor: "text-emerald-700",
        borderColor: "border-emerald-400",
      },
      invited: {
        label: "Invited",
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        borderColor: "border-blue-400",
      },
      suspended: {
        label: "Suspended",
        bgColor: "bg-red-50",
        textColor: "text-red-700",
        borderColor: "border-red-400",
      },
      pending: {
        label: "Pending",
        bgColor: "bg-amber-50",
        textColor: "text-amber-700",
        borderColor: "border-amber-400",
      },
      accepted: {
        label: "Accepted",
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-400",
      },
    };

    return (
      configs[status?.toLowerCase()] || {
        label: status || "Unknown",
        bgColor: "bg-gray-50",
        textColor: "text-gray-700",
        borderColor: "border-gray-400",
      }
    );
  };

  const config = getStatusConfig(status);

  return (
    <div
      className={`inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border transition-all duration-300 ${config.bgColor} ${config.borderColor}`}
    >
      <span
        className={`text-sm font-medium ${config.textColor} capitalize tracking-wide`}
      >
        {config.label}
      </span>
    </div>
  );
};

const PromoterDetails = ({ data: initialData }) => {
  const { event, selectedLanguage } = useOutletContext();
  const { copy } = useClipboard();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [keyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState({
    isOpen: false,
    data: null,
  });

  const { data: details, isLoading } = useQuery({
    queryKey: ["promoter-details", initialData?._id],
    queryFn: async () => {
      const res = await organizationTeamDetails({ id: initialData?._id });
      return res;
    },
    enabled: !!initialData?._id,
  });

  const {
    data,
    isLoading: orderLoading,
    isFetching,
  } = useQuery({
    queryKey: ["promoter-sales", filters, search, initialData?.user],
    queryFn: async () => {
      const res = await orderList({
        viewAs: "seller",
        paymentStatus: "success",
        promoter: initialData?.user,
        salesOnly: true, // Add this flag to get only sales data
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!initialData?.user,
  });

  const {
    data: promoter,
    isLoading: promoterLoading,
    isFetching: promoterFetching,
    refetch,
  } = useQuery({
    queryKey: ["promoter-dashboard", initialData?.user],
    queryFn: async () => {
      const res = await organizationTeamDashboard({
        promoterId: initialData?.user,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
    enabled: !!initialData?.user,
  });

  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Sale Amount"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Customer"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Sale Date"),
      align: "left",
    },
    {
      _id: 5,
      name: t("Details"),
      align: "left",
    },
  ];

  // Table Body Data for Sales
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.promoterInfo?.promoterFee ? (
            <span className="font-semibold">
              ${item?.promoterInfo?.promoterFee}
            </span>
          ) : (
            "-"
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (item?.userData?.firstName ?? "") +
            " " +
            (item?.userData?.lastName ?? ""),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.date).format("MMM DD, YYYY") || "-",
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "details",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setDetailsDrawerOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  const promoterData = details?.data;

  const handleCopyReferralUrl = () => {
    const referralUrl = `${window.location?.origin}/${selectedLanguage}/event-details/${event?.data?.uid}?referral_code=${promoterData?.uid}`;
    copy(referralUrl);
    toast.success("Referral URL copied to clipboard!");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!promoterData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t("No promoter data available")}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gradient-to-br from-gray-50 to-white">
      {/* Basic Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-user mr-3 text-primary"></i>
          {t("Basic Information")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Display Name")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-gray-900 font-medium">
                {promoterData?.info?.displayName || "Not provided"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Email")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-medium text-gray-900">
                {promoterData?.userData?.email || "Not provided"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Status")}
            </label>
            <div>
              <StatusBadge status={promoterData?.status} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Role")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-medium text-gray-900 capitalize">
                {promoterData?.role || "Not specified"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-share-nodes mr-3 text-primary"></i>
          {t("Referral Information")}
        </h3>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Referral Code")}
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 rounded-lg border font-mono text-sm">
                {promoterData?.uid || "Not generated"}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Referral URL")}
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 rounded-lg border text-sm font-medium break-all">
                {promoterData?.uid
                  ? `${window.location?.origin}/${selectedLanguage}/event-details/${event?.data?.uid}?referral_code=${promoterData?.uid}`
                  : "URL not available"}
              </div>
              {promoterData?.uid && (
                <Button
                  buttonLabel="Copy"
                  buttonFunction={handleCopyReferralUrl}
                  buttonIcon="fa-regular fa-link"
                  buttonClasses="px-3 h-10 text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Commission Settings Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-percent mr-3 text-primary"></i>
          {t("Commission Settings")}
        </h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">
                {t("Total Successful Order")}
              </label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {promoter?.totalPromoterOrder || 0}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">
                {t("Total Commission")}
              </label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-sm font-medium text-gray-900 capitalize">
                  ${promoter?.totalPromoterFee || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Per Ticket Settings */}
          {promoterData?.info?.settingsPerTicket?.length > 0 && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-600">
                {t("Ticket-Specific Settings")}
              </label>
              <div className="space-y-3">
                {promoterData.info.settingsPerTicket.map(
                  (ticketSetting, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg border p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium capitalize text-lg text-gray-900">
                          {lngDetect(ticketSetting.ticket?.name) ||
                            "Unnamed Ticket"}
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="text-xs text-blue-600 font-medium mb-1">
                            {t("Discount")}
                          </div>
                          <div className="text-blue-900 font-semibold">
                            {ticketSetting?.discount?.type === "fixed" && "$"}
                            {ticketSetting.discount?.type === "percentage"
                              ? ticketSetting.discount?.value * 100
                              : ticketSetting.discount?.value || 0}
                            {ticketSetting.discount?.type === "percentage" &&
                              "%"}
                          </div>
                        </div>

                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <div className="text-xs text-green-600 font-medium mb-1">
                            {t("Commission")}
                          </div>
                          <div className="text-green-900 font-semibold">
                            {ticketSetting.commission?.type === "fixed" && "$"}
                            {ticketSetting.commission?.type === "percentage"
                              ? ticketSetting.commission?.value * 100
                              : ticketSetting.commission?.value || 0}
                            {ticketSetting.commission?.type === "percentage" &&
                              "%"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-clock mr-3 text-primary"></i>
          {t("Account Information")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Verification Method")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-medium text-gray-900 capitalize">
                {promoterData?.method || "Not specified"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Verified")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span
                className={`font-medium text-sm ${
                  promoterData?.verified ? "text-green-600" : "text-red-600"
                }`}
              >
                {promoterData?.verified ? "Yes" : "No"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Created At")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-medium text-gray-900">
                {promoterData?.createdAt
                  ? dayjs(promoterData.createdAt).format("MMMM D, YYYY h:mm A")
                  : "Not available"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600">
              {t("Verified At")}
            </label>
            <div className="p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-medium text-gray-900">
                {promoterData?.verificationAt
                  ? dayjs(promoterData.verificationAt).format(
                      "MMMM D, YYYY h:mm A"
                    )
                  : "Not verified yet"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Information Card */}
      {promoterData?.organizationData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fa-regular fa-building mr-3 text-primary"></i>
            {t("Organization Information")}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">
                {t("Organization Name")}
              </label>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <span className="text-sm font-medium text-gray-900">
                  {promoterData.organizationData.name?.en || "Not provided"}
                </span>
              </div>
            </div>

            {promoterData.organizationData.description?.en && (
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium text-gray-600">
                  {t("Description")}
                </label>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <span className="text-sm font-medium text-gray-900">
                    {promoterData.organizationData.description.en}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="fa-regular fa-ticket mr-2 text-orange-500"></i>
          {t("Promoter Order")}
        </h3>

        {/* Using the reusable Table component with proper structure */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={orderLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["promoter-sales", filters, search],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          isFetching={isFetching}
        />
      </div>
      <OrderEarningDrawer
        open={detailsDrawerOpen?.isOpen}
        data={detailsDrawerOpen?.data}
        close={() => {
          setDetailsDrawerOpen({ isOpen: false, data: null });
        }}
      />
    </div>
  );
};

export default PromoterDetails;
